import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { isEmpty } from '../../../common/functions';
import { successMsg } from '../../../common/messages';
import { growthStageAdminSort } from '../../../common/sorts';
import { notifyAndTrackError } from '../../../common/error';

import { AuditInfoComponent, CanComponentDeactivate, ProcessingComponent, UnsavedChangesDialogComponent } from '../../../components';

import { AppInsightsService } from '../../../services/app-insights.service';
import { GrowthStageAdminService, IGrowthStage } from '../../../services/insecta';
import { MSGraphService } from '../../../services/msgraph.service';

@Component({
  selector: 'app-growthstage-admin',
  standalone: true,
  imports: [
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    CommonModule,
    DialogModule,
    FloatLabelModule,
    FormsModule,
    InputTextModule,
    RouterModule,
    TableModule,
    ToggleSwitchModule,
    AuditInfoComponent,
    ProcessingComponent,
    UnsavedChangesDialogComponent
  ],

  templateUrl: 'growthstage-admin.component.html'
})
export class GrowthStageAdminComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  data: IGrowthStage[] = [];
  modifiedData: { [s: string]: IGrowthStage } = {};

  dialogConfirmSub: ((leave: boolean) => void) | null = null;

  dialogVars: any = {
    visible: false,
    data: <IGrowthStage>{}
  };

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    showUnsavedChangesDialog: false,
    pageId: `Admin:GrowthStages`,
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Growth Stage Management' }]
  };

  constructor(
    private apiService: GrowthStageAdminService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private router: Router,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:GrowthStages`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/growthstages`);
    this.dialogVars.data = <IGrowthStage>{};
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.pageVars.showUnsavedChangesDialog = true;

      const confirmSub = (decision: boolean) => {
        this.dialogConfirmSub = null;
        resolve(decision);
      };

      this.dialogConfirmSub = confirmSub;
    });
  }

  onDialogConfirm(leave: boolean) {
    this.pageVars.showUnsavedChangesDialog = false;
    if (this.dialogConfirmSub) {
      this.dialogConfirmSub(leave);
    }
  }

  ngOnDestroy() {
    this.data = [];
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);

    const request = {
      profile: this.graphService.getMyProfile$(),
      growthStages: this.apiService.getGrowthStagesAll()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.data = response.growthStages.sort(growthStageAdminSort);
        this.pageVars.currentUser = response.profile.userPrincipalName;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Init:Error'
        });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  backClick() {
    this.router.navigate(['admin/globallists']);
  }

  createDlgCancel() {
    this.dialogVars.visible = false;
    this.dialogVars.data = <IGrowthStage>{};
  }

  createDlgSave() {
    this.dialogVars.data.action = `INS`;
    this.dialogVars.data.actionBy = this.pageVars.currentUser;
    this.dialogVars.data.actionOn = new Date(Date.now());
    const maxSortOrder = Math.max(...this.data.map((x) => x.sortOrder ?? 0), 0);
    this.dialogVars.data.sortOrder = maxSortOrder + 1;
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Insert`, { GrowthStage: this.dialogVars.data });

    firstValueFrom(this.apiService.addGrowthStage(this.dialogVars.data))
      .then((result) => {
        this.dialogVars.data.id = result;
        this.data = [...this.data, this.dialogVars.data];
        successMsg(this.msgService, 'Growth Stage has been created');

        this.dialogVars.visible = false;
        this.dialogVars.data = <IGrowthStage>{};
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Grid:Insert:Error'
        });
      });
  }

  dialogIsValid(): boolean {
    const nameValue = this.dialogVars?.data?.name?.trim() ?? '';
    if (isEmpty(nameValue)) return false;
    if (nameValue === '') return false;

    return true;
  }

  gridNewRow() {
    this.dialogVars.data = <IGrowthStage>{
      isActive: true,
      actionBy: this.pageVars.currentUser
    };

    this.dialogVars.visible = true;
  }

  gridRowEditCancel(c: IGrowthStage, idx: number) {
    this.data[idx] = this.modifiedData[c.id as string];
    delete this.modifiedData[c.id as string];
    this.pageVars.isEditing = false;
  }

  gridRowEditInit(c: IGrowthStage) {
    this.pageVars.isEditing = true;
    this.modifiedData[c.id as string] = { ...c };
    c.action = 'UPD';
    c.actionBy = this.pageVars.currentUser;
    c.actionOn = new Date(Date.now());
  }

  gridRowEditSave(c: IGrowthStage, idx: number) {
    this.pageVars.isEditing = true;
    this.pageVars.isReady = false;

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Save`, { GrowthStage: c });

    firstValueFrom(this.apiService.updateGrowthStage(c))
      .then(() => {
        successMsg(this.msgService, 'Growth Stage is updated');
      })
      .catch((err) => {
        this.data[idx] = this.modifiedData[c.id as string];
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Grid:Save:Error'
        });
      })
      .finally(() => {
        delete this.modifiedData[c.id as string];
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  toggleStatus(item: IGrowthStage) {
    firstValueFrom(this.apiService.updateGrowthStage(item))
      .then(() => {
        const idx = this.data.findIndex((x) => x.id == item.id);
        this.data[idx].isActive = item.isActive;
        this.data[idx].action = 'UPD';
        this.data[idx].actionBy = this.pageVars.currentUser;
        this.data[idx].actionOn = new Date();
        successMsg(this.msgService, 'Growth Stage is updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'ToggleStatus:Error'
        });
      });
  }

  onRowReorder(event: any) {
    if (!event.dragIndex && event.dragIndex !== 0) {
      return;
    }

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Reorder`);
    this.pageVars.isReady = false;

    const updates = this.data.map((item: IGrowthStage, index: number) => {
      item.sortOrder = index + 1;
      item.action = 'UPD';
      item.actionBy = this.pageVars.currentUser;
      item.actionOn = new Date();

      return {
        id: item.id!,
        sortOrder: item.sortOrder!
      };
    });

    firstValueFrom(this.apiService.updateGrowthStageSortOrders(updates, this.pageVars.currentUser))
      .then(() => {
        successMsg(this.msgService, 'Growth Stage order updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'RowReorder:Error'
        });
        this.ngOnInit();
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }
}
